import React, { useState, useEffect, useRef } from 'react';
import { Card, Table, Button, Alert, Space, Typography, Select, Popconfirm, Grid, Tag, theme } from 'antd';
import { SafetyCertificateOutlined, UploadOutlined, DeleteOutlined } from '@ant-design/icons';

const { Title } = Typography;
const { Option } = Select;
const { useBreakpoint } = Grid;

const parseMysqlDateTime = (value) => {
  if (!value) return null;
  const raw = String(value).trim();
  if (!raw) return null;
  if (raw.includes('T')) {
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const parts = raw.split(/[- :]/).filter(Boolean);
  if (parts.length < 3) return null;
  const y = Number(parts[0]);
  const m = Number(parts[1]);
  const d = Number(parts[2]);
  const hh = parts.length >= 4 ? Number(parts[3]) : 0;
  const mm = parts.length >= 5 ? Number(parts[4]) : 0;
  const ss = parts.length >= 6 ? Number(parts[5]) : 0;
  if (!y || !m || !d) return null;
  const date = new Date(y, m - 1, d, hh, mm, ss);
  return Number.isNaN(date.getTime()) ? null : date;
};

const getCsdVigencia = (cert) => {
  const now = new Date();
  const exp = parseMysqlDateTime(cert?.expiration_date);
  if (!exp) return { status: 'unknown', exp: null, daysLeft: null };
  const daysLeft = Math.ceil((exp.getTime() - now.getTime()) / (24 * 60 * 60 * 1000));
  if (exp.getTime() < now.getTime()) return { status: 'expired', exp, daysLeft };
  if (daysLeft <= 30) return { status: 'expiring_soon', exp, daysLeft };
  return { status: 'valid', exp, daysLeft };
};

const CertificatesTab = ({ 
  companies,
  certificates,
  selectedCompany,
  loading,
  onCompanySelect,
  onUpload,
  onDelete
}) => {
  const { token } = theme.useToken();
  const [bottomPaginationVisible, setBottomPaginationVisible] = useState(false);
  const topPaginationRef = useRef(null);
  const screens = useBreakpoint();

  const certList = Array.isArray(certificates) ? certificates : [];
  const certVigencias = certList.map((c) => getCsdVigencia(c));
  const hasAnyCert = certList.length > 0;
  const hasValidCert = certVigencias.some((v) => v.status === 'valid' || v.status === 'expiring_soon');
  const hasExpiredCert = certVigencias.some((v) => v.status === 'expired');
  const hasExpiringSoonCert = certVigencias.some((v) => v.status === 'expiring_soon');

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setBottomPaginationVisible(!entry.isIntersecting && entry.boundingClientRect.top < 0);
      },
      { threshold: 0 }
    );

    if (topPaginationRef.current) {
      observer.observe(topPaginationRef.current);
    }

    return () => observer.disconnect();
  }, [selectedCompany]);

  const certificateColumns = [
    {
      title: 'Número de Certificado',
      dataIndex: 'certificate_number',
      key: 'certificate_number',
    },
    {
      title: 'Vigencia',
      dataIndex: 'expiration_date',
      key: 'expiration_date',
      render: (_, record) => {
        const v = getCsdVigencia(record);
        if (v.status === 'expired') {
          return (
            <Tag color="red">
              Vencido{v.exp ? ` (${v.exp.toLocaleDateString('es-MX')})` : ''}
            </Tag>
          );
        }
        if (v.status === 'expiring_soon') {
          return (
            <Tag color="orange">
              Por vencer{v.exp ? ` (${v.exp.toLocaleDateString('es-MX')})` : ''}
            </Tag>
          );
        }
        if (v.status === 'valid') {
          return (
            <Tag color="green">
              Vigente{v.exp ? ` (${v.exp.toLocaleDateString('es-MX')})` : ''}
            </Tag>
          );
        }
        return <Tag>Sin fecha</Tag>;
      },
    },
    {
      title: 'Fecha de Subida',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Acciones',
      key: 'actions',
      render: (_, record) => (
        <Popconfirm
          title="¿Estás seguro de eliminar este certificado?"
          description="Esta acción no se puede deshacer."
          onConfirm={() => onDelete(record.id)}
          okText="Sí"
          cancelText="No"
        >
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
          >
            Eliminar
          </Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <Card>
      <div style={{ marginBottom: '16px' }}>
        <Title level={4} style={{ margin: 0, marginBottom: '16px' }}>
          Certificados Digitales
        </Title>
        
        <Space direction={screens.xs ? 'vertical' : 'horizontal'} style={{ marginBottom: '16px', width: screens.xs ? '100%' : 'auto' }}>
          <Select
            placeholder="Selecciona una empresa"
            style={{ width: screens.xs ? '100%' : 400 }}
            value={selectedCompany?.id}
            onChange={onCompanySelect}
          >
            {companies.map(company => (
              <Option key={company.id} value={company.id}>
                {company.tax_name} - {company.tax_id}
              </Option>
            ))}
          </Select>
          
          <Button
            type="primary"
            icon={<UploadOutlined />}
            disabled={!selectedCompany}
            onClick={onUpload}
            block={screens.xs}
          >
            Subir Certificado
          </Button>
        </Space>
      </div>

      <Alert
        message="Información"
        description="Los certificados digitales son necesarios para timbrar facturas. Debes subir tanto el archivo .cer como el .key junto con la contraseña."
        type="info"
        showIcon
        style={{ marginBottom: '16px' }}
      />

      {selectedCompany && (
        <>
          {hasAnyCert && !hasValidCert ? (
            <Alert
              message="Certificado CSD vencido"
              description="Tus certificados cargados están vencidos. Sube un CSD vigente para poder timbrar."
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
            />
          ) : hasAnyCert && hasExpiringSoonCert ? (
            <Alert
              message="Certificado CSD por vencer"
              description="Tienes un certificado próximo a vencer. Considera subir uno vigente para evitar errores al timbrar."
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />
          ) : null}
          <div ref={topPaginationRef} />
          <Table
            columns={certificateColumns}
            dataSource={certificates}
            rowKey="id"
            loading={loading}
            locale={{ emptyText: 'No hay certificados subidos para esta empresa' }}
            pagination={{
              position: bottomPaginationVisible ? ['topRight', 'bottomRight'] : ['topRight'],
            }}
            scroll={{ x: 'max-content' }}
          />
        </>
      )}

      {!selectedCompany && (
        <div style={{ textAlign: 'center', padding: '40px', color: token.colorTextTertiary }}>
          Selecciona una empresa para ver sus certificados
        </div>
      )}
    </Card>
  );
};

export default CertificatesTab;
